import {
  LocalStorageKey,
  codegenTypeKey,
  copyFromProjectKey,
  githubTokenKey,
  storageViewAsKey,
} from "@/wab/client/LocalStorageKey";
import { getCaptchaToken } from "@/wab/client/captcha";
import { ensureIsTopFrame, isHostFrame } from "@/wab/client/cli-routes";
import {
  SerializableClipboardData,
  serializeClipboardItems,
} from "@/wab/client/clipboard/ReadableClipboard";
import { LocalClipboardAction } from "@/wab/client/clipboard/local";
import { createAssistantModelRequests } from "@/wab/client/copilot/assistant-model-requests";
import { analytics } from "@/wab/client/observability";
import { PushPullQueue } from "@/wab/commons/asyncutil";
import { PromisifyMethods } from "@/wab/commons/promisify-methods";
import { transformErrors } from "@/wab/shared/ApiErrors/errors";
import {
  ApiDataSource,
  ApiUser,
  CAPTCHA_TOKEN_HEADER,
  ProjectId,
} from "@/wab/shared/ApiSchema";
import { fullName } from "@/wab/shared/ApiSchemaUtil";
import { LowerHttpMethod } from "@/wab/shared/HttpClientUtil";
import {
  PkgInfo,
  SharedApi,
  WrappedStorageEvent,
} from "@/wab/shared/SharedApi";
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from "@/wab/shared/api/socket";
import { Bundler } from "@/wab/shared/bundler";
import {
  assert,
  ensure,
  hackyCast,
  maybe,
  omitNils,
  swallow,
  truncateText,
} from "@/wab/shared/common";
import {
  DEVFLAGS,
  flattenInsertableIconGroups,
  flattenInsertableTemplates,
} from "@/wab/shared/devflags";
import { PLEXUS_STORAGE_KEY } from "@/wab/shared/insertables";
import * as Sentry from "@sentry/browser";
import { ProxyMarked, proxy, releaseProxy } from "comlink";
import type {ConfiguredGitResponseCapture} from "@/wab/shared/copilot/assistant-configured-git-response-custody";
import $ from "jquery";
import L, { pick } from "lodash";
import { Socket, connect } from "socket.io-client";

const fullApiPath = (url: /*TWZ*/ string) => `/api/v1/${L.trimStart(url, "/")}`;

export class XHRStatus0Error extends Error {
  constructor(message?: string) {
    super(`XHRStatus0Error: ${message}`);
  }
}

export class UnknownApiError extends Error {}

export const ajax = async (
  method: string,
  apiSubpath: string,
  data?: {},
  opts?: /*TWZ*/ {},
  hideDataOnError?: boolean,
  noErrorTransform?: boolean,
  signal?: AbortSignal,
) =>
  new Promise<any>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("Assistant HTTP request canceled before dispatch"));
      return;
    }
    let search = "";
    if (method === "get" || method === "delete") {
      search = new URLSearchParams(
        L.mapValues(L.omitBy(data, L.isUndefined), (v) => JSON.stringify(v)),
      ).toString();
      if (search) {
        search = "?" + search;
      }
    }
    // From jQuery.ajax().fail() we can't get the full stack trace with
    // information about who made the request. To overcome that, we create
    // an empty error here to get a stack trace that an be appended to the
    // stack of the error we generate in the fail() function below.
    const callingStack = (new Error().stack || "")
      .split("\n")
      .slice(1)
      .join("\n");

    const params =
      data instanceof FormData && data.has("file")
        ? {
            processData: false,
            // This is imperative to force jQuery not to add a Content-Type,
            // leaving that to the browser which can handle FormData properly.
            // Reference: https://stackoverflow.com/questions/5392344/
            contentType: false as any,
            data,
          }
        : {
            mimeType: "application/json",
            contentType: "application/json; charset=utf-8",
            data: method === "get" ? undefined : maybe(data, JSON.stringify),
          };

    const request = $.ajax({
      url: fullApiPath(apiSubpath) + search,
      type: method,
      ...params,
      ...opts,
    })
      .done((responseData, _txtStatus, _xhr) => {
        resolve(responseData);
      })
      .fail((xhr, txtStatus, err) => {
        const error =
          swallow(() => {
            const response = JSON.parse(xhr.responseText);
            if (noErrorTransform) {
              return response;
            }
            let transformed = transformErrors(response.error);
            if (!(transformed instanceof Error)) {
              // The error was JSON-parsible, but not one of the known
              // ApiErrors. So it is now just a JSON object, not an Error.
              // We create an UnknownApiError for it instead.
              transformed = new UnknownApiError(
                `${method} ${apiSubpath} failed: ${
                  (transformed as Error).message
                }`,
              );
            }
            return transformed;
          }) ||
          (xhr.status === 0
            ? new XHRStatus0Error(`${method} ${apiSubpath} failed`)
            : new UnknownApiError(
                `${method} ${apiSubpath} failed [${xhr.status}/${
                  xhr.readyState
                }]: ${err}\nstatus ${txtStatus}\ndata ${
                  data === undefined
                    ? "undefined"
                    : hideDataOnError
                      ? "[redacted]"
                      : truncateText(200, JSON.stringify(data))
                }`,
              ));
        if (!noErrorTransform) {
          error.stack += `\n${callingStack}`;
        }
        reject(error);
      });
    const abort = () => {
      request.abort();
    };
    signal?.addEventListener("abort", abort, { once: true });
    request.always(() => signal?.removeEventListener("abort", abort));
    if (signal?.aborted) abort();
  });

export class Api extends SharedApi {
  /** Only the original per-call marked response sink crosses Comlink. Release
   * its remote endpoint after HTTP settlement/capture; no global observer. */
  async prepareAssistantConfiguredGit(projectId:string,raw:Parameters<SharedApi["prepareAssistantConfiguredGit"]>[1],capture?:ConfiguredGitResponseCapture){
    try{return await super.prepareAssistantConfiguredGit(projectId,raw,capture);}
    finally{if(capture){const endpoint=capture as ConfiguredGitResponseCapture & {[releaseProxy]?:()=>void};endpoint[releaseProxy]?.();}}
  }
  async executeAssistantConfiguredGit(projectId:string,raw:Parameters<SharedApi["executeAssistantConfiguredGit"]>[1],capture?:ConfiguredGitResponseCapture){
    try{return await super.executeAssistantConfiguredGit(projectId,raw,capture);}
    finally{if(capture){const endpoint=capture as ConfiguredGitResponseCapture & {[releaseProxy]?:()=>void};endpoint[releaseProxy]?.();}}
  }

  /** Project-bound metadata only. Settings never cross the host-frame bridge. */
  async listProjectDataSourceMetadata(
    projectId: string,
  ): Promise<Pick<ApiDataSource, "id" | "name" | "source">[]> {
    const { project } = await this.getSiteInfo(projectId);
    assert(project.id === projectId, "Data-source discovery project changed");
    const rows = await this.listDataSources(project.workspaceId ?? undefined);
    return rows.flatMap((row) =>
      row.dataSources.map((source) => ({
        id: source.id,
        name: source.name,
        source: source.source,
      })),
    );
  }
  private assistantModelRequests = createAssistantModelRequests();
  private publishingRequests = createAssistantModelRequests();
  private nativeCodegenRequests = createAssistantModelRequests();
  async cancelNativeCodegenRequest(projectId: string, requestId: string) {
    this.nativeCodegenRequests.cancel(JSON.stringify([projectId, requestId]));
  }
  async cancelPublishingBookPdf(projectId: string, requestId: string) {
    this.publishingRequests.cancel(JSON.stringify([projectId, requestId]));
  }
  async cancelAssistantModelTurn(projectId: string, requestId: string) {
    this.assistantModelRequests.cancel(JSON.stringify([projectId, requestId]));
  }
  constructor() {
    super();
    ensureIsTopFrame();
  }

  setUser = setUser;

  clearUser() {
    analytics().setAnonymousUser();
    Sentry.configureScope((scope) => {
      scope.setUser({});
    });
  }

  async req(
    method: LowerHttpMethod,
    url: string,
    data?: {},
    opts?: { headers?: { [name: string]: string }; assistantRequestId?: string; publishingRequestId?: string; codegenRequestId?: string },
    hideDataOnError?: boolean,
    noErrorTransform?: boolean,
  ) {
    const { assistantRequestId, publishingRequestId, codegenRequestId, ...ajaxOpts } = opts ?? {};
    if (codegenRequestId)
      return this.nativeCodegenRequests.execute(codegenRequestId, signal =>
        ajax(method, url, data, ajaxOpts, hideDataOnError, noErrorTransform, signal));
    if (publishingRequestId)
      return this.publishingRequests.execute(publishingRequestId, (signal) =>
        ajax(
          method,
          url,
          data,
          ajaxOpts,
          hideDataOnError,
          noErrorTransform,
          signal,
        ),
      );
    if (assistantRequestId)
      return this.assistantModelRequests.execute(assistantRequestId, (signal) =>
        ajax(
          method,
          url,
          data,
          ajaxOpts,
          hideDataOnError,
          noErrorTransform,
          signal,
        ),
      );
    return ajax(method, url, data, ajaxOpts, hideDataOnError, noErrorTransform);
  }

  private socket?: Socket<ServerToClientEvents, ClientToServerEvents>;
  private queue = new PushPullQueue<{ eventName: string; data: unknown }>();

  /**
   * This method proxies the websocket, creating it if needed. This is a very
   * simple model where we assume a single socket and a static set of events
   * that are being listened for - basically just enough to serve
   * StudioCtx.startListeningForSocketEvents.
   *
   * Each call returns one EventWithData bundle, or undefined once someone
   * calls closeSocket. So you're expected to call this in a loop.
   */
  async listenSocket(
    eventNames: (keyof ServerToClientEvents)[],
  ): Promise<{ eventName: string; data: unknown } | undefined> {
    if (!this.socket) {
      this.socket = connect({
        path: "/api/v1/socket",
        transports: ["websocket"],
      });
      for (const eventName of eventNames) {
        this.socket.on(eventName, (data) => {
          this.queue.push({ eventName, data });
        });
      }
    }
    return this.queue.pull();
  }

  async closeSocket() {
    this.socket?.close();
    this.socket = undefined;

    // Close existing queue before reassigning.
    this.queue.close();
    this.queue = new PushPullQueue();
  }

  async getStudioUrl(): Promise<string> {
    return `${location.protocol}//${location.host}`;
  }

  async reloadLocation() {
    location.reload();
  }

  async emit<K extends keyof ClientToServerEvents>(
    eventName: K,
    data: Parameters<ClientToServerEvents[K]>[0],
  ) {
    // @ts-expect-error - ClientToServerEvents always has 1 param, so this is safe
    ensure(this.socket, "Unexpected nullish socket").emit(eventName, data);
  }

  protected async captchaToken(
    action: string,
  ): Promise<{ [CAPTCHA_TOKEN_HEADER]: string } | undefined> {
    const token = await getCaptchaToken({ action });
    return token ? { [CAPTCHA_TOKEN_HEADER]: token } : undefined;
  }

  protected githubToken() {
    const token = this.getStorageItem(githubTokenKey);
    if (!token) {
      throw new Error("Missing GitHub token");
    }
    return { "x-plasmic-github-token": token };
  }

  // listeners is a dict of unique IDs to registered handlers.
  // It is used to help removing event listeners, because handler/event
  // wrapping makes it very hard to get the right handler to remove.
  //
  // Expected usage of (add|remove)StorageListener:
  //
  //   React.useEffect(() => {
  //     const uniqueId = mkUuid();
  //     api.addStorageListener(uniqueId, handler);
  //     return () => api.removeEventListener("storage", uniqueId);
  //   }
  private listeners: Record<string, (...args: any) => any> = {};

  addStorageListener(
    uniqueId: string,
    listener: ((ev: WrappedStorageEvent) => any) & ProxyMarked,
  ) {
    const wrapped = (ev: StorageEvent): any => {
      return listener({
        key: ev.key,
        newValue: ev.newValue,
      });
    };
    this.listeners[uniqueId] = wrapped;
    window.addEventListener("storage", wrapped);
  }

  // This is for generic listeners which do not require arguments. API will
  // not forward the triggering event to the handler.
  addEventListener(
    event: string,
    uniqueId: string,
    listener: (() => any) & ProxyMarked,
  ) {
    assert(["popstate"].includes(event), "Unexpected event");
    const wrapped = () => {
      return listener();
    };
    this.listeners[uniqueId] = wrapped;
    window.addEventListener(event, wrapped);
  }

  removeEventListener(event: string, uniqueId: string) {
    window.removeEventListener(event, this.listeners[uniqueId]);
    delete this.listeners[uniqueId];
  }

  addStorageItem(key: LocalStorageKey, value: any) {
    assert(!isHostFrame(), "Should only run in the top frame");
    localStorage.setItem(key, value);
    // "storage" events aren't triggered on the same window that sets the
    // new value
    window.dispatchEvent(
      new StorageEvent("storage", {
        key,
        newValue: value,
      }),
    );
  }

  getStorageItem(key: string) {
    assert(!isHostFrame(), "Should only run in the top frame");
    return localStorage.getItem(key);
  }

  removeStorageItem(key: string) {
    assert(!isHostFrame(), "Should only run in the top frame");
    localStorage.removeItem(key);
  }

  async readNavigatorClipboard(
    lastAction: LocalClipboardAction,
  ): Promise<SerializableClipboardData> {
    assert(!isHostFrame(), "Should only run in the top frame");

    let permission: PermissionStatus;
    try {
      permission = await navigator.permissions.query({
        name: "clipboard-read",
      });
    } catch (_) {
      throw new Error("Your browser does not support Clipboard API");
    }

    if (permission.state != "granted" && permission.state != "prompt") {
      throw new Error("No permission to use clipboard");
    }

    assert(
      navigator.clipboard.read,
      "Your browser does not support Clipboard API",
    );
    const items = await navigator.clipboard.read();
    return await serializeClipboardItems(items, lastAction);
  }

  async whitelistProjectIdToCopy(projectId: string) {
    assert(!isHostFrame(), "Should only run in the top frame");

    this.addStorageItem(
      copyFromProjectKey(projectId as ProjectId),
      JSON.stringify(+new Date()),
    );
  }
}

export class SiteApi {
  _siteId: string;
  _api: PromisifyMethods<Api>;
  constructor({ siteId, api }: { siteId: string; api: PromisifyMethods<Api> }) {
    this._siteId = siteId;
    this._api = api;
  }
  dml(data) {
    return this._api.post(`/dml/${this._siteId}`, data);
  }
  insert(data) {
    return this._api.post(`/insert/${this._siteId}`, data);
  }
  query(queries: QueryBundle) {
    return this._api.post(`/query/${this._siteId}`, { queries });
  }
  eval(data) {
    return this._api.post("/eval", data);
  }
  uploadUrl() {
    return fullApiPath(`/upload/${this._siteId}`);
  }
  nodeOp(data) {
    return this._api.post("/node-op", data);
  }
}

interface QueryMap {
  [key: string]: string;
}
type QueryBundle = QueryMap | string[];

export class BundlingSiteApi {
  _siteApi: SiteApi;
  _bundler: Bundler;
  constructor({ bundler, siteApi }: { bundler: Bundler; siteApi: SiteApi }) {
    this._siteApi = siteApi;
    this._bundler = bundler;
  }
  dml(data) {
    return this._siteApi.dml(data);
  }
  insert(data) {
    return this._siteApi.insert(data);
  }
  query(queries: QueryBundle) {
    return this._siteApi.query(queries);
  }
  eval(data) {
    return this._siteApi.eval(data);
  }
  uploadUrl() {
    return this._siteApi.uploadUrl();
  }
}

/**
 * This is to filter the API requests from the host app, for example to make
 * sure it won't be able to delete projects, or to access all projects from the
 * user.
 * @param projectId The site ID
 * @param apiProxy A proxy that binds the methods to the original API instance
 * @param appConfig
 * @returns Another proxy with basic access control to prevent unwanted calls
 */
export function filteredApi(
  projectId: string,
  apiProxy: PromisifyMethods<Api>,
  appConfig: typeof DEVFLAGS,
) {
  // Cached Pkg
  let maybePkg: PkgInfo | undefined = undefined;
  const getPkgId = async () => {
    if (!maybePkg) {
      // Maybe the project didn't have a pkg last time we fetched, so try again!
      maybePkg = (await apiProxy.getPkgByProjectId(projectId)).pkg;
    }
    return maybePkg?.id;
  };

  const insertableProjectIds = L.uniq([
    ...flattenInsertableTemplates(appConfig.insertableTemplates).map(
      (i) => i.projectId,
    ),
    ...flattenInsertableIconGroups(appConfig.insertableTemplates).map(
      (i) => i.projectId,
    ),
    ...flattenInsertableTemplates(DEVFLAGS.insertableTemplates).map(
      (i) => i.projectId,
    ),
    ...flattenInsertableIconGroups(DEVFLAGS.insertableTemplates).map(
      (i) => i.projectId,
    ),
  ]);

  const checkprojectId = (reqProjectId: string) => {
    assert(
      reqProjectId === projectId,
      `Unexpected projectId ${reqProjectId} (Expected ${projectId})`,
    );
  };

  // Whitelisted keys for the host app to read and write to the localStorage
  // Make sure to not allow reading auth tokens etc.
  const whitelistedLocalStorageKeys: string[] = [
    codegenTypeKey,
    storageViewAsKey(projectId as ProjectId),
  ];
  const whitelistedLocalStorageKeyPrefixes = [
    PLEXUS_STORAGE_KEY,
    "plasmic.tours.",
    "plasmic.focused.",
    "plasmic.leftTabKey",
    "plasmic.free-trial.",
    "plasmic.load-cache.",
  ];

  // Methods that are always safe for the host app to call
  const whitelistedMethods: (keyof Api)[] = [
    "listenSocket",
    "closeSocket",
    "getStudioUrl",
    "getUsersById",
    "reloadLocation",
    "addEventListener",
    "removeEventListener",
    "getSelfInfo",
    "getPlumePkg",
    "getPkgByProjectId",
    "getPkgVersion",
    "getProjectRevision",
    "listUnpublishedProjectRevisions",
    "revertProjectToRevision",
    "getPkgVersionMeta",
    "refreshCsrfToken",
    "getLastBundleVersion",
    "getAppConfig",
    "getClip",
    "readNavigatorClipboard",
    "uploadImageFile",
    "queryCopilot",
    "sendCopilotFeedback",
    "addReactionToComment",
    "removeReactionFromComment",
    "getAppAuthPubConfig",
    "processSvg",
  ];
  const checkProjectIdInFirstArg =
    (f: any) =>
    (reqProjectId: string, ...args: any[]) => {
      checkprojectId(reqProjectId);
      return f(reqProjectId, ...args);
    };

  // Comlink has already decoded an optional marked sink before this guard
  // executes. A foreign project never reaches Api's method-level finally, so
  // release its exact received endpoint here without invoking it or dispatching.
  const checkConfiguredProjectIdAndResponseCapture =
    (f: any) =>
    (reqProjectId: string, raw: unknown, capture?: ConfiguredGitResponseCapture) => {
      try {
        checkprojectId(reqProjectId);
      } catch (error) {
        if (capture) {
          try {
            const endpoint = capture as ConfiguredGitResponseCapture & {
              [releaseProxy]?: () => void;
            };
            endpoint[releaseProxy]?.();
          } catch {
            // A caller-supplied unusable/already-released sink cannot replace
            // the original native project refusal or admit any dispatch.
          }
        }
        throw error;
      }
      return f(reqProjectId, raw, capture);
    };

  const isWhitelistedStorageKey = (key: string) => {
    return (
      whitelistedLocalStorageKeys.includes(key) ||
      whitelistedLocalStorageKeyPrefixes.some((prefix) =>
        key.startsWith(prefix),
      )
    );
  };

  // Methods that we need to allow the host to use, but want to make sure it's
  // being used correctly
  const wrappedMethods: Partial<{
    [key in keyof Api]: (f: Api[key]) => Api[key];
  }> = {
    emit: (f) => (eventName, data) => {
      if (data && "projectIds" in data) {
        assert(
          (data["projectIds"] as string[]).every((id) => id === projectId),
          "Unexpected projectId",
        );
      }
      if (data && "projectId" in data) {
        assert(data["projectId"] === projectId, "Unexpected projectId");
      }
      return f(eventName, data);
    },
    addStorageListener: (f) => (uniqueId, listener) => {
      return f(
        uniqueId,
        proxy((ev) => {
          if (isWhitelistedStorageKey(ev.key ?? "")) {
            listener(ev);
          }
        }),
      );
    },
    addStorageItem: (f) => (key, value) => {
      assert(isWhitelistedStorageKey(key), "Unexpected key " + key);
      return f(key, value);
    },
    getStorageItem: (f) => (key) => {
      assert(isWhitelistedStorageKey(key), "Unexpected key " + key);
      return f(key);
    },
    removeStorageItem: (f) => (key) => {
      assert(isWhitelistedStorageKey(key), "Unexpected key " + key);
      return f(key);
    },
    getModelUpdates: checkProjectIdInFirstArg,
    getSiteInfo: (f) => async (siteId, opts) => {
      // The only projects we need to fetch are the site itself and the
      // insertable templates (imported projects use `getPkgVersion`).

      const isKnownProject = [projectId, ...insertableProjectIds].includes(
        siteId,
      );
      if (isKnownProject) {
        return f(siteId, opts);
      }

      // In case the user has copied some elements from another project we
      // check how long ago the user copied the elements. If it's been more
      // than 1 day, we don't need to fetch the project.
      const errorMsg = `Unexpected projectId ${siteId}`;

      const value = await apiProxy.getStorageItem(
        copyFromProjectKey(siteId as ProjectId),
      );
      if (!value) {
        throw new Error(errorMsg);
      }

      const diff = new Date().getTime() - new Date(+value).getTime();
      const diffInDays = diff / (1000 * 3600 * 24);

      if (diffInDays > 1) {
        throw new Error(errorMsg);
      }

      return f(siteId, opts);
    },
    cloneProject: checkProjectIdInFirstArg,
    saveProjectRevChanges: checkProjectIdInFirstArg,
    requestAssistantModelTurn: checkProjectIdInFirstArg,
    cancelAssistantModelTurn: checkProjectIdInFirstArg,
    authorPublishingBook: checkProjectIdInFirstArg,
    exportPublishingBookPdf: checkProjectIdInFirstArg,
    cancelPublishingBookPdf: checkProjectIdInFirstArg,
    computeNextProjectVersion: checkProjectIdInFirstArg,
    previewProjectBranchMerge: checkProjectIdInFirstArg,
    prepareProjectBranchMerge: checkProjectIdInFirstArg,
    executeProjectBranchMerge: checkProjectIdInFirstArg,
    readProjectBranchMerge: checkProjectIdInFirstArg,
    cancelProjectBranchMerge: checkProjectIdInFirstArg,
    listProjectBranchMerges: checkProjectIdInFirstArg,
    prepareProjectPublicationEffect: checkProjectIdInFirstArg,
    executeProjectPublicationEffect: checkProjectIdInFirstArg,
    readProjectPublicationEffect: checkProjectIdInFirstArg,
    cancelProjectPublicationEffect: checkProjectIdInFirstArg,
    listProjectPublicationEffects: checkProjectIdInFirstArg,
    publishProject: checkProjectIdInFirstArg,
    prepareAssistantConfiguredGit: checkConfiguredProjectIdAndResponseCapture,
    executeAssistantConfiguredGit: checkConfiguredProjectIdAndResponseCapture,
    readAssistantConfiguredGit: checkProjectIdInFirstArg,
    cancelAssistantConfiguredGit: checkProjectIdInFirstArg,
    listAssistantConfiguredGit: checkProjectIdInFirstArg,

    listPkgVersionsWithoutData:
      (f) =>
      async (...args) => {
        assert(args[0] === (await getPkgId()), "Unexpected pkgId");
        return f(...args);
      },
    updatePkgVersion:
      (f) =>
      async (...args) => {
        assert(args[0] === (await getPkgId()), "Unexpected pkgId");
        return f(...args);
      },
    revertToVersion: checkProjectIdInFirstArg,
    getProjectRevWithoutData: checkProjectIdInFirstArg,
    listBranchesForProject: checkProjectIdInFirstArg,
    createBranch: checkProjectIdInFirstArg,
    readProjectBranchLifecycleSource: checkProjectIdInFirstArg,
    prepareProjectBranchLifecycleEffect: checkProjectIdInFirstArg,
    executeProjectBranchLifecycleEffect: checkProjectIdInFirstArg,
    readProjectBranchLifecycleEffect: checkProjectIdInFirstArg,
    cancelProjectBranchLifecycleEffect: checkProjectIdInFirstArg,
    listProjectBranchLifecycleEffects: checkProjectIdInFirstArg,
    prepareProjectBranchRenameEffect: checkProjectIdInFirstArg,
    renamePreparedProjectBranch: checkProjectIdInFirstArg,
    readProjectBranchRenameEffect: checkProjectIdInFirstArg,
    cancelProjectBranchRenameEffect: checkProjectIdInFirstArg,
    listProjectBranchRenameEffects: checkProjectIdInFirstArg,
    prepareAssistantCmsDraftWrite: checkProjectIdInFirstArg,
    executeAssistantCmsDraftWrite: checkProjectIdInFirstArg,
    readAssistantCmsDraftEffect: checkProjectIdInFirstArg,
    cancelAssistantCmsDraftEffect: checkProjectIdInFirstArg,
    listAssistantCmsDraftEffects: checkProjectIdInFirstArg,
    uploadAssistantCmsMedia: checkProjectIdInFirstArg,
    readAssistantOwnedCmsDraft: checkProjectIdInFirstArg,
    prepareAssistantCmsPublication: checkProjectIdInFirstArg,
    executeAssistantCmsPublication: checkProjectIdInFirstArg,
    readAssistantCmsPublication: checkProjectIdInFirstArg,
    cancelAssistantCmsPublication: checkProjectIdInFirstArg,
    listAssistantCmsPublications: checkProjectIdInFirstArg,
    readAssistantCmsPublicationSource: checkProjectIdInFirstArg,
    prepareAssistantCmsOperation: checkProjectIdInFirstArg,
    executeAssistantCmsOperation: checkProjectIdInFirstArg,
    readAssistantCmsOperation: checkProjectIdInFirstArg,
    cancelAssistantCmsOperation: checkProjectIdInFirstArg,
    listAssistantCmsOperations: checkProjectIdInFirstArg,
    readAssistantCmsOperationSource: checkProjectIdInFirstArg,

    updateBranch: checkProjectIdInFirstArg,
    deleteBranch: checkProjectIdInFirstArg,
    getComments: checkProjectIdInFirstArg,
    postRootComment: checkProjectIdInFirstArg,
    postThreadComment: checkProjectIdInFirstArg,
    editComment: checkProjectIdInFirstArg,
    editThread: checkProjectIdInFirstArg,
    deleteComment: checkProjectIdInFirstArg,
    deleteThread: checkProjectIdInFirstArg,
    updateNotificationSettings: checkProjectIdInFirstArg,
    listAppUsers: checkProjectIdInFirstArg,
    getEndUserRoleInApp: checkProjectIdInFirstArg,
    listAppRoles: checkProjectIdInFirstArg,
    prepareNativeCodegenJob: checkProjectIdInFirstArg,
    executeNativeCodegenJob: checkProjectIdInFirstArg,
    readNativeCodegenJob: checkProjectIdInFirstArg,
    cancelNativeCodegenJob: checkProjectIdInFirstArg,
    listNativeCodegenJobs: checkProjectIdInFirstArg,
    readNativeCodegenManifest: checkProjectIdInFirstArg,
    readNativeCodegenFile: checkProjectIdInFirstArg,
    readNativeCodegenSource: checkProjectIdInFirstArg,
    cancelNativeCodegenRequest: checkProjectIdInFirstArg,
    readAssistantAuthConfig: checkProjectIdInFirstArg,
    readAssistantProjectSharing: checkProjectIdInFirstArg,
    previewAssistantProjectAccess: checkProjectIdInFirstArg,
    prepareProjectAccessEffect: checkProjectIdInFirstArg,
    executeProjectAccessEffect: checkProjectIdInFirstArg,
    readProjectAccessEffect: checkProjectIdInFirstArg,
    cancelProjectAccessEffect: checkProjectIdInFirstArg,
    listProjectAccessEffects: checkProjectIdInFirstArg,
    readAssistantWorkspaceCmsMetadata: checkProjectIdInFirstArg,
    listProjectDataSourceMetadata: checkProjectIdInFirstArg,
    getAppCurrentUserProperties: checkProjectIdInFirstArg,
    getInitialUserToViewAs: checkProjectIdInFirstArg,
    upsertAppCurrentUserOpConfig: checkProjectIdInFirstArg,
    getAppCurrentUserOpConfig: checkProjectIdInFirstArg,
    getDataSourceOpId: checkProjectIdInFirstArg,
    executeDataSourceOperationInCanvas: checkProjectIdInFirstArg,
    executeDataSourceReadOperationInCanvas: checkProjectIdInFirstArg,
    executeDataSourceStudioOp: checkProjectIdInFirstArg,
    getDataSourceById:
      (innerGetDataSourceById) =>
      async (
        dataSourceId: string,
        opts: Parameters<typeof apiProxy.getDataSourceById>[1],
      ) => {
        return innerGetDataSourceById(dataSourceId, {
          ...(opts ?? {}),
          // Enforce that the host app doesn't return data source settings
          excludeSettings: true,
        });
      },
    setMainBranchProtection: checkProjectIdInFirstArg,
    whitelistProjectIdToCopy: checkProjectIdInFirstArg,
  };

  // Start with all methods marked as forbidden
  const filtered = Object.fromEntries(
    Object.getOwnPropertyNames(Object.getPrototypeOf(apiProxy)).map((key) => [
      key,
      (..._args: any[]) => {
        throw new Error("unauthorizedMethod " + key);
      },
    ]),
  ) as any as Api;

  // Pick the whitelisted methods
  Object.assign(filtered, pick(apiProxy, whitelistedMethods));

  // Assign the validation wrappers
  Object.entries(wrappedMethods).forEach(([key, wrapper]: any) => {
    filtered[key] = wrapper(apiProxy[key]);
  });

  return filtered;
}

export function setUser(user: ApiUser) {
  const { id, email, firstName, lastName } = user;
  const traits = omitNils({
    email,
    firstName,
    lastName,
    createdAt: hackyCast<string>(user.createdAt),
    fullName: fullName(user),
    domain: email.split("@")[1],
  });
  analytics().setUser(id);
  Sentry.configureScope((scope) => {
    scope.setUser({ id, ...traits });
  });
}

export function invalidationKey(method: string, ...args: any[]) {
  return JSON.stringify([method, ...args]);
}

export function apiKey<
  Method extends keyof Api,
  Args extends (Api[Method] extends (..._args: any[]) => any
    ? Parameters<Api[Method]>
    : never),
>(method: Method, ...args: Args) {
  return invalidationKey(method, ...args);
}
